import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Progress, Select, Space, Table } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { TableDownload } from "../../components/TableDownload";
import { ColumnPicker, useColumnPicker } from "../../components/ColumnPicker";
import { PrintFilters } from "../../components/PrintFilters";
import { api } from "../../helpers/api";
import { useProjectsQuery } from "../../helpers/queries";
import type { ExportColumns } from "../../helpers/csv";
import type { ProjectItemStatusRow } from "../../api-models";

const consumedPct = (r: ProjectItemStatusRow) => (r.approvedQty > 0 ? Math.round((r.consumedQty / r.approvedQty) * 100) : 0);

// One definition behind both the table and its export.
const COLUMNS: ExportColumns<ProjectItemStatusRow> = [
  { title: "Code", dataIndex: "itemCode" },
  { title: "Item", dataIndex: "itemName" },
  { title: "Approved", dataIndex: "approvedQty" },
  { title: "Consumed", dataIndex: "consumedQty" },
  {
    title: "% consumed",
    render: (_, r) => {
      const pct = consumedPct(r);
      return <Progress percent={pct} status={pct > 100 ? "exception" : undefined} size="small" />;
    },
    // A progress bar exports as the number it is drawing.
    exportValue: (r) => `${consumedPct(r)}%`,
  },
];

export default function BomVsActualPage() {
  const { data: projects = [] } = useProjectsQuery();
  // The project lives in the URL so the dashboard's over-consumption rows can open this
  // report already pointed at the project whose overrun they name.
  const [params, setParams] = useSearchParams();
  const projectId = params.has("projectId") ? Number(params.get("projectId")) : undefined;
  const setProjectId = (id?: number) =>
    setParams(id ? { projectId: String(id) } : {}, { replace: true });
  const [rows, setRows] = useState<ProjectItemStatusRow[]>([]);
  const cols = useColumnPicker("report:bom-vs-actual", COLUMNS);

  useEffect(() => {
    if (!projectId) return setRows([]);
    api.get<ProjectItemStatusRow[]>("/reports/bom-vs-actual", { params: { projectId } }).then((res) => setRows(res.data));
  }, [projectId]);

  const project = projects.find((p) => p.id === projectId);
  // One description of the view, read by both the export header and the printed page.
  const filters = { project: project ? `${project.code} — ${project.name}` : "" };

  return (
    <div>
      <PageHeader
        title="BOM vs Actual"
        extra={
          <Space>
            <ColumnPicker state={cols} />
            <TableDownload
              name="BOM vs Actual"
              columns={cols.visible}
              dataSource={rows}
              disabled={!rows.length}
              filters={filters}
              beforeDownload={() => (projectId ? null : "Select a project first.")}
            />
          </Space>
        }
      />
      <div className="glut-filter-bar">
        <Select
          placeholder="Select a project"
          style={{ width: 280 }}
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
          value={projectId}
          onChange={setProjectId}
        />
      </div>
      <PrintFilters filters={filters} />
      <Table rowKey="itemId" dataSource={rows} columns={cols.visible} />
    </div>
  );
}
